import type { Member, Recipe } from './schema';

/**
 * Qui voit quoi, qui modifie quoi — **la règle, à un seul endroit** (US-43,
 * déplacée ici par le correctif d'US-49).
 *
 * Elle vivait dans `server/src/access/visibleRecipes.ts`, ce qui suffisait tant
 * qu'aucun écran n'avait à la connaître. L'interface du tri en lot en a besoin :
 * elle doit savoir ce qu'elle a le droit de proposer, sinon elle propose des
 * gestes sans effet. La recopier côté client aurait produit deux définitions
 * qui divergent — exactement ce que le §1 de `CLAUDE.md` interdit pour la
 * visibilité.
 *
 * **Ce fichier ne remplace aucun contrôle serveur.** Une interface qui grise un
 * bouton n'est pas de la confidentialité : `visibilityClause` (SQL) et les
 * routes restent seuls juges. Ce qui est ici sert à ne pas *promettre* ce qui
 * sera refusé.
 */

/** Le strict miroir de `visibilityClause` : partagée, ou la sienne. */
export function canSee(
  viewer: Pick<Member, 'id'>,
  recipe: Pick<Recipe, 'visibility' | 'owner_id'>,
): boolean {
  return recipe.visibility === 'shared' || recipe.owner_id === viewer.id;
}

/**
 * Voir n'est pas modifier, et c'est tout l'objet des deux colonnes : une
 * recette partagée se lit par tous et ne se modifie que par son propriétaire.
 * Le rôle `lecture` ne modifie rien, y compris ses propres recettes — mais il
 * tient son journal, qui n'est pas une modification de la recette.
 */
export function canEdit(
  viewer: Pick<Member, 'id' | 'role'>,
  recipe: Pick<Recipe, 'owner_id'>,
): boolean {
  if (viewer.role === 'lecture') return false;
  // Une recette sans propriétaire est une recette d'avant la migration `010`
  // qui n'a pas encore été adoptée : le propriétaire de l'instance peut la
  // reprendre, personne d'autre.
  if (recipe.owner_id === null) return viewer.role === 'proprietaire';
  return recipe.owner_id === viewer.id;
}
